'use client'

import { cx } from 'classix'
import { IconToggleCheckOncheck } from 'icons/components/IconToggleCheckOncheck'
import { IconToggleDefault } from 'icons/components/IconToggleDefault'
import { IconToggleUndeterminated } from 'icons/components/IconToggleUndeterminated'
import { ClientLabel } from 'labels/components/ClientLabel/ClientLabel'
import type { ChangeEvent, ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import styles from './Checkbox.module.css'

export interface CheckboxProps {
	readonly id: string
	readonly className?: string
	readonly label: ReactNode
	readonly name?: string
	readonly checked?: boolean
	readonly undeterminated?: boolean
	readonly disabled?: boolean
	readonly hasExtendedContent?: boolean
	readonly errorMessage?: string
	readonly errorPlaceholders?: Record<string, string | number>
	readonly onChange?: (event: ChangeEvent<HTMLInputElement>) => void
	readonly value?: string
	readonly dataTestId?: string
}

export const Checkbox = (props: CheckboxProps): ReactNode => {
	const {
		checked = false,
		undeterminated = false,
		disabled = false,
		errorMessage,
		errorPlaceholders,
		id,
		label,
		name = id,
		className = '',
		onChange = () => {
			/* do nothing */
		},
		hasExtendedContent = false,
		dataTestId,
		...checkboxAttributes
	} = props

	const [isChecked, setIsChecked] = useState(checked)

	function handleClick() {
		if (undeterminated) {
			return
		}
		setIsChecked(!isChecked)
	}

	useEffect(() => {
		setIsChecked(checked)
	}, [checked])

	const renderIcon = useMemo(() => {
		const iconClassName = cx(
			styles.icon,
			errorMessage && styles.error,
			disabled && styles.disabled
		)
		if (isChecked && undeterminated) {
			return (
				<IconToggleUndeterminated
					className={iconClassName}
					color='current-color'
				/>
			)
		}

		if (isChecked) {
			return (
				<IconToggleCheckOncheck
					className={iconClassName}
					color='current-color'
				/>
			)
		}

		return <IconToggleDefault className={iconClassName} color='current-color' />
	}, [isChecked, undeterminated])

	return (
		<div className={cx(className)}>
			<label
				className={cx(
					styles.box,
					hasExtendedContent && styles.top,
					disabled && styles.disabled
				)}
			>
				<input
					checked={isChecked}
					className={styles.input}
					disabled={disabled}
					id={id}
					name={name}
					onChange={onChange}
					onClick={handleClick}
					type='checkbox'
					data-testid={dataTestId}
					{...checkboxAttributes}
				/>
				{renderIcon}
				<div className={styles.label}>{label}</div>
			</label>
			{errorMessage && (
				<span
					className={cx(styles.textError, errorMessage && styles.showError)}
				>
					<ClientLabel placeholderValues={errorPlaceholders}>
						{errorMessage}
					</ClientLabel>
				</span>
			)}
		</div>
	)
}
